import { useEffect, useMemo, useRef, useState } from 'react';

import { fetchPoisInBoundaryJson } from 'data/search/poi/fetch-pois-in-boundary-json';
import type { PoiBoundaryResponse } from 'data/search/poi/types';
import type { PoiCategoryId } from 'data/search/poi/poi-categories';
import { superCategoriesFromSelectedIds } from 'data/search/poi/poi-categories';
import type { Bounds } from 'components/search-es/types';


export const POI_MAP_BOUNDS_DEBOUNCE_MS = 250;

const DEFAULT_POI_BOUNDS_LIMIT = 200;

export type PoiMapBoundsFetchState =
  | { status: 'idle'; data: null; error: null }
  | { status: 'loading'; data: PoiBoundaryResponse | null; error: null }
  | { status: 'success'; data: PoiBoundaryResponse; error: null }
  | { status: 'error'; data: PoiBoundaryResponse | null; error: Error };

const initialState: PoiMapBoundsFetchState = { status: 'idle', data: null, error: null };


export function usePoiMapBoundsFetch(params: {
  bounds: Bounds | null;
  selectedPoiCategoryIds: ReadonlySet<PoiCategoryId>;
  country?: string;
}) {
  const { bounds, selectedPoiCategoryIds: selectedPoiCategories, country } = params;
  const [state, setState] = useState<PoiMapBoundsFetchState>(initialState);

  const abortRef = useRef<AbortController | null>(null);
  const sequenceRef = useRef(0);
  const lastSuccessRef = useRef<{ selectionKey: string; data: PoiBoundaryResponse } | null>(null);

  const selectionKey = useMemo(() => [...selectedPoiCategories].sort().join(','), [selectedPoiCategories]);

  useEffect(() => {
    if (selectedPoiCategories.size === 0) {
      abortRef.current?.abort();
      abortRef.current = null;
      lastSuccessRef.current = null;
      setState(initialState);
      return;
    }

    if (bounds == null) {
      return;
    }

    const timer = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const seq = ++sequenceRef.current;
      // Always keep last known data visible while the new request is in-flight,
      // even if the category selection changed — prevents marker flash on toggle.
      const stale = lastSuccessRef.current?.data ?? null;
      setState({ status: 'loading', data: stale, error: null });

      const superCategories = superCategoriesFromSelectedIds(selectedPoiCategories);

      void fetchPoisInBoundaryJson({
        bounds,
        superCategories,
        country,
        limit: DEFAULT_POI_BOUNDS_LIMIT,
        signal: controller.signal,
      })
        .then(data => {
          if (seq !== sequenceRef.current) {
            return;
          }

          lastSuccessRef.current = { selectionKey, data };
          setState({ status: 'success', data, error: null });
        })
        .catch(err => {
          if ((err as Error).name === 'AbortError') {
            return;
          }
          if (seq !== sequenceRef.current) {
            return;
          }
          console.warn('[POI bounds] request failed', err);
          const staleOnError = lastSuccessRef.current?.data ?? null;
          setState({
            status: 'error',
            data: staleOnError,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        });
    }, POI_MAP_BOUNDS_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [bounds, selectionKey, country, selectedPoiCategories]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return state;
}
